import {
    createBillingRule,
    deleteBillingRule,
    getAllBillingRules,
    getAllFirmBillingRules,
    updateBillingRule,
} from './http/labBilling';
import type { FetchQueryOptions, MutationOptions, QueryKey, UseQueryOptions } from '@tanstack/react-query';
import type { BillingRule, BillingResponse, NewBillingRule } from '@thinkalpha/locates-ws-client';
import type { ReactBindings } from 'src/types/bindings';

export { updateBillingRule };

/** Per-list snapshot of a single rule's pre-mutation value, used for granular rollbacks. */
export type BillingRuleSnapshot = ReadonlyArray<readonly [QueryKey, BillingRule | undefined]>;

/**
 * QueryKey Constants
 */
export const LAB_BILLING_RULES = 'lab-billingRules';

/**
 * Query for retrieving all billing rules for a superuser.
 */
export const getAllBillingRulesQuery = (
    options: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllBillingRules>>, Error, BillingRule>>,
): FetchQueryOptions<Awaited<ReturnType<typeof getAllBillingRules>>> => {
    return {
        ...options,
        queryKey: [LAB_BILLING_RULES],
        queryFn: getAllBillingRules,
    };
};

/**
 * Query for retrieving all billing rules for the firm
 */
export const getAllFirmBillingRulesQuery = (
    firmId: string,
    options: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllFirmBillingRules>>, Error, BillingRule>>,
): FetchQueryOptions<Awaited<ReturnType<typeof getAllFirmBillingRules>>> => {
    return {
        ...options,
        queryKey: [LAB_BILLING_RULES, firmId],
        queryFn: () => getAllFirmBillingRules(firmId),
    };
};

/**
 * Mutation for creating a billing rule.
 */
export const createBillingRuleMutation = (): MutationOptions<
    Awaited<ReturnType<typeof createBillingRule>>,
    Error,
    NewBillingRule
> => {
    return {
        mutationFn: createBillingRule,
    };
};

/** Cancel any in-flight billing-rule list refetches before an optimistic write. */
export const cancelBillingRulesQueries = (queryClient: ReactBindings['QueryClient']) =>
    queryClient.cancelQueries({ queryKey: [LAB_BILLING_RULES] });

/** Capture the cached value of `ruleId` across every list query so it can be restored on rollback. */
export const snapshotBillingRule = (queryClient: ReactBindings['QueryClient'], ruleId: string): BillingRuleSnapshot =>
    queryClient
        .getQueriesData<BillingResponse>({ queryKey: [LAB_BILLING_RULES] })
        .map(([queryKey, list]) => [queryKey, list?.rules.find((r) => r.id === ruleId)] as const);

/** Replace `rule` in every cached list (no-op for lists that don't contain it). */
export const writeBillingRuleToCache = (queryClient: ReactBindings['QueryClient'], rule: BillingRule) =>
    queryClient.setQueriesData<BillingResponse>({ queryKey: [LAB_BILLING_RULES] }, (prev) =>
        prev ? { ...prev, rules: prev.rules.map((r) => (r.id === rule.id ? rule : r)) } : prev,
    );

/** Splice the snapshot's value back in for `ruleId`, leaving every other rule untouched. */
export const restoreBillingRuleFromSnapshot = (
    queryClient: ReactBindings['QueryClient'],
    ruleId: string,
    snapshot: BillingRuleSnapshot,
) => {
    for (const [queryKey, previous] of snapshot) {
        if (previous === undefined) continue;
        queryClient.setQueryData<BillingResponse>(queryKey, (current) =>
            current ? { ...current, rules: current.rules.map((r) => (r.id === ruleId ? previous : r)) } : current,
        );
    }
};

/**
 * Mutation for deleting a billing rule.
 */
export const deleteBillingRuleMutation = (): MutationOptions<
    Awaited<ReturnType<typeof deleteBillingRule>>,
    Error,
    BillingRule,
    { previousBillingRule: BillingRule; billingRule: BillingRule }
> => {
    return {
        mutationFn: deleteBillingRule,
    };
};

/**
 * Invalidation for billing rules, or a specific billing rule if the ID is provided.
 */
export const invalidateBillingRules = (
    queryClient: ReactBindings['QueryClient'],
    billingRuleId: string | undefined = undefined,
) => {
    return billingRuleId
        ? queryClient.invalidateQueries({ queryKey: [LAB_BILLING_RULES, billingRuleId] })
        : queryClient.invalidateQueries({ queryKey: [LAB_BILLING_RULES] });
};

/**
 * Appends a newly created billing rule to the cached list so the table reflects it
 * without a refetch. Picks the superuser-wide or firm-scoped cache key to match
 * `getAllBillingRulesQuery` / `getAllFirmBillingRulesQuery`. No-op if the list query
 * has no cached data yet (the next fetch will include the new rule).
 */
export const addBillingRuleToCache = (
    queryClient: ReactBindings['QueryClient'],
    rule: BillingRule,
    options: { superUserMode: boolean; firmId: string | undefined },
) => {
    const queryKey = options.superUserMode ? [LAB_BILLING_RULES] : [LAB_BILLING_RULES, options.firmId];
    queryClient.setQueryData<BillingResponse>(queryKey, (prev) =>
        prev ? { ...prev, rules: [...prev.rules, rule] } : prev,
    );
};

/**
 * Optimistic update for a billing rule: snapshot the rule's current cached value, patch the list
 * cache with the new value, restore (per-rule, never wholesale) on error, overwrite with the server
 * response on success, and invalidate on settle so the table reflects the saved rule (including
 * cleared optional fields) without a manual refresh.
 */
export const updateBillingRuleMutation = (
    queryClient: ReactBindings['QueryClient'],
): MutationOptions<
    Awaited<ReturnType<typeof updateBillingRule>>,
    Error,
    BillingRule,
    { previousRules: BillingRuleSnapshot }
> => {
    return {
        mutationFn: updateBillingRule,
        onMutate: async (billingRule) => {
            await cancelBillingRulesQueries(queryClient);
            const previousRules = snapshotBillingRule(queryClient, billingRule.id);
            writeBillingRuleToCache(queryClient, billingRule);
            return { previousRules };
        },
        onError: (_err, billingRule, context) => {
            if (!context) return;
            restoreBillingRuleFromSnapshot(queryClient, billingRule.id, context.previousRules);
        },
        onSuccess: (response) => {
            writeBillingRuleToCache(queryClient, response.rule);
        },
        onSettled: () => {
            invalidateBillingRules(queryClient);
        },
    };
};
